import { Component, DestroyRef, Inject, Input } from '@angular/core';
import { PhoneNumberSelectionDialogComponent } from '../../ai-assistant-components/phone-number-selection-dialog/phone-number-selection-dialog.component';
import { PhoneSettingsResponse } from '../../../models/responses/phone-settings-response';
import { CONFIRMATION_DIALOG_TEXT, ConfirmationDialogComponent } from '../../general-dialogs-components/confirmation-dialog/confirmation-dialog.component';
import { ImportTwilioPhoneNumberDialogComponent } from '../import-twilio-phone-number-dialog/import-twilio-phone-number-dialog.component';
import { PhoneSettingsService } from '../../../services/phone-settings.service';
import { SubscriptionHelper } from '../../../classes/subscription-helper';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UserPhoneNumbersPageEventsService } from '../../../events/user-integrations-page-events.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgFor, NgIf } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AiAgentResponse } from '../../../models/responses/ai-agent-response';
import { AgentType } from '../../../models/enums/agent-type';
import { AiConfigurationPageEventsService } from '../../../events/ai-configuration-page-events.service';
import { PhoneSettingsType } from '../../../models/enums/phone-settings-type';
import { UserResponse } from '../../../models/responses/user-response';
import { UserService } from '../../../services/user.service';
import { WarningDialogComponent } from '../../general-dialogs-components/warning-dialog/warning-dialog.component';

@Component({
  selector: 'app-user-phone-numbers',
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatButtonModule,
    MatIconModule,
  ],
  templateUrl: './user-phone-numbers.component.html',
  styleUrl: './user-phone-numbers.component.scss'
})
export class UserPhoneNumbersComponent {
  @Input() aiAgent: AiAgentResponse | undefined;
  outBoundType: AgentType = AgentType.OUTBOUND;
  phoneSettings: PhoneSettingsResponse[] = [];
  isLoading: boolean = false;
  isSelectLoading: boolean = false;
  subscriptions: SubscriptionHelper;
  connectedUser!: UserResponse;

  constructor(
    private phoneSettingsService: PhoneSettingsService,
    private dialog: MatDialog,
    private userPhoneNumberPageEventService: UserPhoneNumbersPageEventsService,
    private aiConfigurationPageEventsService: AiConfigurationPageEventsService,
    private userService: UserService,
    private destroyRef: DestroyRef,
    private snackBar: MatSnackBar
  ) {
    this.subscriptions = new SubscriptionHelper(destroyRef);
  }

  ngOnInit() {
    this.initPhoneSettingsList();
    this.subscribeToOnPhoneNumberRefreshEvent();
  }

  subscribeToOnPhoneNumberRefreshEvent() {
    this.subscriptions.add(this.userPhoneNumberPageEventService.OnPhoneNumbersRefreshEvent.subscribe(() => {
      this.initPhoneSettingsList();
    }));
  }

  fetchConnectedUser() {
    this.userService.fetchLoggedUser().subscribe({
      next: user => {
        this.connectedUser = user;
      },
      error: () => {
        alert('Server error contact support');
      }
    })
  }

  initPhoneSettingsList() {
    this.isLoading = true;

    this.phoneSettingsService.fetchUserPhoneNumbers().subscribe({
      next: phoneSettings => {
        if (this.aiAgent)
          this.phoneSettings = phoneSettings.filter(phone => !phone.aiAgentId);
        else
          this.phoneSettings = phoneSettings;

        this.isLoading = false;

        this.fetchConnectedUser();
      },
      error: () => {
        alert('Server error contact support');
        this.isLoading = false;
      }
    });
  }

  onSelectClicked(phoneSettings: PhoneSettingsResponse) {
    if (this.aiAgent && this.aiAgent.agentType == this.outBoundType && phoneSettings.phoneSettingsType == PhoneSettingsType.BOUGHT) {
      this.dialog.open(WarningDialogComponent, {
        width: '25%',
        autoFocus: false,
        data: { title: 'Phone selection', data: 'For security purposes, you cannot select a phone that is bought for an outbound agent you can only select the imported one' }
      });
    } else {
      this.dialog.open(ConfirmationDialogComponent, {
        autoFocus: false,
        width: '25%',
        data: { title: 'Select phone', data: 'Are you sure you want to assign this phone number to this assistant? Currently assigned phone number will be removed' }
      }).afterClosed().subscribe(result => {
        if (result == CONFIRMATION_DIALOG_TEXT) {
          this.onAssignPhoneNumberToAi(phoneSettings);
        }
      });
    }
  }

  onAssignPhoneNumberToAi(phoneSettings: PhoneSettingsResponse) {
    if (this.aiAgent) {
      this.isSelectLoading = true;

      this.phoneSettingsService.assignPhoneNumberToAiAgent(phoneSettings.id!, this.aiAgent?.id).subscribe({
        next: (phoneSettings) => {
          this.isSelectLoading = false;
          this.openSnackBar('Phone number assigned to ai successfully');
          this.aiConfigurationPageEventsService.onSelectPhoneNumberEvent.emit(phoneSettings);
        },
        error: () => {
          this.isSelectLoading = false;
          alert("Server error contact support")
        }
      });
    }
  }

  onImportTwilioPhoneNumber() {
    this.dialog.open(ImportTwilioPhoneNumberDialogComponent, {
      autoFocus: false,
      width: '50%'
    });
  }

  onUpdateTwilioPhoneNumber(phoneSettings: PhoneSettingsResponse) {
    this.dialog.open(ImportTwilioPhoneNumberDialogComponent, {
      autoFocus: false,
      width: '50%',
      data: { phoneSettings }
    });
  }

  onDeleteTwilioPhoneNumber(phoneSettings: PhoneSettingsResponse) {
    this.dialog.open(ConfirmationDialogComponent, {
      autoFocus: false,
      width: '25%',
      data: {
        title: 'Remove Phone Number', data: `Removing the phone number will remove it from the assistant if it is assigned to one.
         If you bought a free phone number it will be removed and you won\'t be able to have another free one.
         Are you sure you want to remove the phone number?` }
    }).afterClosed().subscribe(result => {
      if (result == CONFIRMATION_DIALOG_TEXT) {
        this.isLoading = true;

        this.phoneSettingsService.removeUserPhoneNumber(phoneSettings.id!).subscribe({
          next: () => {
            this.initPhoneSettingsList()
            this.isLoading = false;
            this.openSnackBar('Phone number removed successfully')
          },
          error: () => {
            alert('Server error contact support');
            this.isLoading = false;
          }
        });
      }
    });
  }

  openTwilioPhoneNumberSelectionDialog() {
    if (this.connectedUser.hadFreePhone) {
      this.dialog.open(WarningDialogComponent, {
        width: '50%',
        autoFocus: false,
        data: { title: 'Phone warning', data: 'You cannot purchase another phone number you already had your free one' }
      });
    } else {
      this.dialog.open(WarningDialogComponent, {
        width: '50%',
        autoFocus: false,
        data: { title: 'Phone warning', data: 'We are offering you one free phone number, after that you won\'t be able to get another free one' }
      }).afterClosed().subscribe(() => {
        this.dialog.open(PhoneNumberSelectionDialogComponent, {
          width: '80%',
          autoFocus: false
        });
      })
    }

  }

  onPurchasePhoneNumber(phoneNumber: string) {
    this.isLoading = true;

    this.phoneSettingsService.purchasePhoneNumber(phoneNumber).subscribe({
      next: () => {
        this.initPhoneSettingsList();
        this.openSnackBar('Phone number purchased successfully');
        this.isLoading = false;
      },
      error: () => {
        alert('Server error, contact support');
        this.isLoading = false;
      }
    });
  }

  openSnackBar(message: string) {
    this.snackBar.open(message, 'close', { duration: 5000 });
  }
}
